import { getToken } from "../api/login";
import { useNavigate } from "react-router-dom";
import { useStore } from "../store";
import { Button, Form, Alert, Container, Card, Image, Row, Col, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import axios from "axios";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { Eye, EyeOff } from "lucide-react";
import logo from "../image/logo-no-background.png";
import myVideo from "../image/my_video.mp4";
import CookieConsent from "react-cookie-consent";

type User = {
  email: string;
  password: string;
};

type MessageState = {
  type: "success" | "danger" | null;
  text: string | undefined;
};

export default function Login() {
  const { setAuth } = useStore();
  const navigate = useNavigate();
  const { t } = useTranslation("login");
  const [message, setMessage] = useState<MessageState>({ type: null, text: undefined });

  const handleLogin = async (user: User) => {
    try {
      const response = await getToken(user);
      if (response.status === 200) {
        localStorage.setItem("tokenLida", response.data.access_token);
        setAuth(true);
        navigate("/upload");
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        setMessage({
          type: "danger",
          text:
            status === 404
              ? t("InvalidData")
              : status === 401
              ? t("ActivationError")
              : t("UnknownError"),
        });
      } else {
        setMessage({ type: "danger", text: t("UnknownError") });
      }
    }
  };

  const handleGoogleLoginSuccess = async (response: any) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_HOST_TEST}auth/google/callback?token=${response.credential}`
      );
      localStorage.setItem("tokenLida", res.data.access_token);
      setAuth(true);
      navigate("/upload");
    } catch {
      setMessage({ type: "danger", text: t("GoogleLoginFailed") });
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik<User>({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().email(t("InvalidEmail")).required(t("Required")),
      password: Yup.string().required(t("Required")),
    }),
    onSubmit: handleLogin,
  });

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID!}>
          {/* Cookie Disclaimer */}
      <CookieConsent
        location="bottom"
        buttonText="I understand"
        cookieName="cookieConsent"
        style={{ background: "#2B373B" }}
        buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
        expires={150}
      >
        This website uses cookies to enhance the user experience.
      </CookieConsent>
      <Container fluid className="vh-100 p-0">
        <Row className="h-100 m-0">
          {/* Left column: video for desktop */}
          <Col md={8} className="d-none d-md-flex p-0">
            <video
              autoPlay
              muted
              loop
              className="w-100 h-100"
              style={{ objectFit: "contain", backgroundColor: "black" }}
            >
              <source src={myVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </Col>

          {/* Right column: login form */}
          <Col xs={12} md={4} className="d-flex align-items-center justify-content-center">
            <Card className="p-4 shadow" style={{ width: "100%", maxWidth: "400px" }}>
              <div className="text-center mb-3">
                <Image
                  src={logo}
                  alt="Logo"
                  fluid
                  style={{ maxWidth: "150px", display: "block", margin: "0 auto" }}
                />
              </div>
              <h2 className="text-center mb-2" style={{ fontWeight: "bold", fontSize: "1.75rem" }}>
                {"Lan4ai.com"}
              </h2>
              <p className="text-center text-muted mb-4">{t("Your personal data analyst.")}</p>
              {message.text && <Alert variant={message.type ?? "danger"}>{message.text}</Alert>}
              <Form onSubmit={formik.handleSubmit}>
                <Form.Group controlId="email" className="mb-3">
                  <Form.Label>{t("Email")}</Form.Label>
                  <Form.Control
                    type="email"
                    {...formik.getFieldProps("email")}
                    isInvalid={!!formik.errors.email && formik.touched.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Password Field with Eye Icon */}
                <Form.Group controlId="password" className="mb-3">
                  <Form.Label>{t("Password")}</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      {...formik.getFieldProps("password")}
                      isInvalid={!!formik.errors.password && formik.touched.password}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.password}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                <Button type="submit" variant="primary" className="w-100 mb-3">
                  {t("LoginButton")}
                </Button>
              </Form>
              <p className="text-center">
                <a href="/forgot-password" className="text-decoration-none">
                  {t("ForgotPassword")}
                </a>
              </p>
              <GoogleLogin
                onSuccess={handleGoogleLoginSuccess}
                onError={() => setMessage({ type: "danger", text: t("GoogleLoginFailed") })}
              />
              <hr />
              <p className="text-center">
                {t("NoAccount")}{" "}
                <Button variant="link" onClick={() => navigate("/Registration")}>
                  {t("SignUp")}
                </Button>
              </p>
            </Card>
          </Col>
        </Row>
      </Container>
    </GoogleOAuthProvider>
  );
}
